// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import _ from 'lodash';
import { axios } from "./axios";
import { showError, showSuccess } from "../actions/globalActions";

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const useRegistrationTeamStore = create(immer((set) => ({
  registrationTeams: [],
  // Soft-deleted teams for the admin "Deleted teams" restore section — kept
  // separate from `registrationTeams` so the main (live-only) view is unaffected.
  deletedRegistrationTeams: [],

  // Methods to manipulate the state
  setTeamRegistrations: (registrationTeams) => {
    set((state) => {
      state.registrationTeams = registrationTeams;
    });
  },

  // Asynchronous actions
  getAllRegistrationBySeasonId: async (id) => {
    try {
      const { data } = await axios.get(`${baseUrl}/registrationTeams/season/${id}/all`);
      set((state) => {
        state.registrationTeams = data.result;
      });
    } catch (error) {
      console.error("Error in getAllRegistrationBySeasonId:", error);
    }
  },

  removeTeamRegistration: (getToken) => {
    return async (id, includeTeam = false) => {
      try {
        const token = await getToken();
        const { data } = await axios.delete(`${baseUrl}/registrationTeams/d/${id}?includeTeam=${includeTeam ? 1 : 0}`, createAuthHeader(token));
        // Success toast is shown by the caller (RegistrationAdmin) as an Undo toast.
        set((state) => {
          state.registrationTeams = state.registrationTeams.filter(tr => tr._id !== id);
        });
      } catch (error) {
        // Re-throw so the caller can surface the backend reason (e.g. "fully refund
        // first"). Swallowing it here made the caller's await resolve as success —
        // showing a false "removed" toast while the team stayed.
        console.error("Error in removeTeamRegistration:", error);
        throw error;
      }
    }
  },

  // Merge duplicate teams (same players, same division) into the earliest — the
  // backend re-points payments and soft-deletes the extras. Returns the survivor id.
  consolidateTeam: (getToken) => {
    return async (id) => {
      const token = await getToken();
      const { error, result, status } = (await axios.post(`${baseUrl}/registrationTeams/d/${id}/consolidate`, {}, createAuthHeader(token))).data;
      if (status === 0) {
        showError("Error", error || "Could not consolidate teams");
        throw new Error(error || "Could not consolidate teams");
      }
      showSuccess("Success", "Duplicate teams consolidated");
      return result;
    }
  },

  // Free-agent pool assignment (ADR 0054): place selected pool entries onto a
  // DivisionTeam — an existing one ({ divisionTeamId }) or a brand-new one
  // ({ newTeam: { name, divisionId } }). The backend re-points each free
  // agent's Player, stamps the RT accepted + assignedDivisionTeam, and emails
  // the player. Caller refetches the registration teams afterwards.
  assignFreeAgents: (getToken) => {
    return async ({ organization, freeAgentIds, target }) => {
      const token = await getToken();
      const { error, result, status } = (await axios.post(
        `${baseUrl}/registrationTeams/d/assignFreeAgents`,
        { organization, freeAgentIds, target },
        createAuthHeader(token),
      )).data;
      if (status === 0) {
        showError("Error", error || "Could not assign free agents");
        throw new Error(error || "Could not assign free agents");
      }
      return result;
    }
  },

  getRegistrationTeamsByRegistrationId: async (id, token) => {
    try {
      const { data } = await axios.get(`${baseUrl}/registrationTeams/registration/${id}/all?include=division,requestedDivision,tournament,contest,requestedContest,divisionTeam,user(email,name),registration(_id)`,  createAuthHeader(token));
      set((state) => {
        state.registrationTeams = _.unionBy(data.result, state.registrationTeams, '_id');
      });
    } catch (error) {
      console.error("Error in getAllRegistrationTeamById:", error);
    }
  },

  // Soft-deleted teams for this registration (same enriched shape as the live list).
  getDeletedTeams: async (id, token) => {
    try {
      const { data } = await axios.get(`${baseUrl}/registrationTeams/registration/${id}/all?deleted=1&include=division,requestedDivision,tournament,contest,requestedContest,divisionTeam,user(email,name),registration(_id)`, createAuthHeader(token));
      set((state) => {
        state.deletedRegistrationTeams = data.result || [];
      });
    } catch (error) {
      console.error("Error in getDeletedTeams:", error);
    }
  },

  // Un-soft-delete a removed team (+ its DivisionTeam). Shared by the Undo toast and
  // the Deleted-teams section's Restore button. Registration-scoped endpoint.
  restoreTeam: (getToken) => {
    return async (registrationId, teamId) => {
      const token = await getToken();
      const { error, result, status } = (await axios.post(`${baseUrl}/registrationTeams/registration/${registrationId}/restoreTeam`, { teamId }, createAuthHeader(token))).data;
      if (status === 0) {
        showError("Error", error || "Could not restore team");
        throw new Error(error || "Could not restore team");
      }
      showSuccess("Success", "Team restored");
      set((state) => {
        state.deletedRegistrationTeams = state.deletedRegistrationTeams.filter(t => t._id !== teamId);
      });
      return result;
    }
  },

  getRegistrationTeamById: async (id, token) => {
    try {
      const { data } = await axios.get(`${baseUrl}/registrationTeams/${id}?include=division,playerz`, createAuthHeader(token));
      set((state) => {
        const index = state.registrationTeams.findIndex(tr => tr._id === id);
        if (index !== -1) {
          state.registrationTeams[index] = data.result;
        } else {
          state.registrationTeams.push(data.result);
        }
      });
    } catch (error) {
      console.error("Error in getRegistrationTeamById:", error);
    }
  },

  updateTeamRegistration: async (teamRegistration = {}, token, cb) => {
    try {
      const { data } = await axios.put(`${baseUrl}/registrationTeams/d/${teamRegistration._id}`, teamRegistration, createAuthHeader(token));
      showSuccess("Success", "Successfully Updated");
      set((state) => {
        const index = state.registrationTeams.findIndex(tr => tr._id === teamRegistration._id);
        if (index !== -1) {
          state.registrationTeams[index] = data.result;
        }
      });
      console.log('updateTeamRegistration::cb:', data.result);
      cb && cb(data.result);
    } catch (error) {
      showError("Error", "Update Error");
      console.error("Error in updateTeamRegistration:", error);
    }
  },

  // Admin status dropdown. A dedicated endpoint rather than updateTeamRegistration
  // because status has server-side side effects (DivisionTeam visibility, waitlist
  // reconciliation) and the generic update whitelists no fields.
  updateTeamStatus: async (id, status, token, cb) => {
    try {
      const { data } = await axios.put(`${baseUrl}/registrationTeams/d/${id}/status`, { status }, createAuthHeader(token));
      set((state) => {
        const index = state.registrationTeams.findIndex(tr => tr._id === id);
        if (index !== -1) {
          state.registrationTeams[index] = { ...state.registrationTeams[index], ...data.result };
        }
      });
      cb && cb(data.result);
    } catch (error) {
      showError("Error", "Could not update status");
      console.error("Error in updateTeamStatus:", error);
    }
  },

  // Read-only pre-move check for moving this registrationTeam into `divisionId`.
  // Returns { ok, reasons, isFull }: ok/reasons = target division's eligibility
  // (gender/age/rating, player-name-prefixed, overridable); isFull = target is at
  // capacity (a hard block — caller voids the move). Never mutates. On network
  // error, fails OPEN ({ ok: true, isFull: false }) so a transient failure doesn't
  // block a move.
  checkDivisionEligibility: async (registrationTeamId, divisionId, token) => {
    try {
      const { data } = await axios.post(
        `${baseUrl}/registrationTeams/d/${registrationTeamId}/checkDivisionEligibility`,
        { divisionId },
        createAuthHeader(token),
      );
      return data.result || { ok: true, reasons: [], isFull: false };
    } catch (error) {
      console.error("Error in checkDivisionEligibility:", error);
      return { ok: true, reasons: [], isFull: false };
    }
  },

  addTeamRegistration: async (payload = {}) => {
    try {
      const { data } = await axios.post(`${baseUrl}/registrationTeams`, payload);
      showSuccess("Success", "Successfully Added");
      set((state) => {
        state.registrationTeams.push(data.result);
      });
      return data.result;
    } catch (error) {
      showError("Error", "Add Error");
      console.error("Error in addTeamRegistration:", error);
      throw error;
    }
  },

  acceptTeamRegistration: (getToken) => {
    return async (id, team, players = []) => {
      // Logic for accepting team registration...
      try {
        const token = await getToken();
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        const { error, result, status } = (await axios.post(`${baseUrl}/registrationTeams/d/${id}/requestTeamAccept`, { team, players }, config)).data;
        if (error) {
          showError("Error", "Duplicate Name Error");
        } else {
          showSuccess("Success", "Successfully Added");
          set((state) => {
            const index = state.registrationTeams.findIndex(tr => tr._id === id);
            if (index !== -1) {
              state.registrationTeams[index] = result;
            }
          });
        }

      } catch (error) {
        showError("Error", "Accept Error");
        console.error("Error in acceptTeamRegistration:", error);
      }
    }
  },

  sendEmail: async ({ player, type, message, id, leagueName }) => {
    // Logic for sending email...
  },

  sendTeamEmail: async ({ registrationTeam, type, message, id, leagueName }) => {
    // Logic for sending team email...
  },

  requestUpload: async (id, { email, title, msg }) => {
    try {
      await axios.post(`${baseUrl}/registrationTeams/${id}/requestUpload`);
      showSuccess("Success", "Request Sent");
    } catch (error) {
      console.error("Error in requestUpload:", error);
    }
  },

  // promoteFromWaitlist / moveToWaitlist removed: capacity state is derived
  // from team data (see packages/shared/utils/waitlist.ts), not an admin
  // override. Auto-promotion happens via the cron when a team is removed
  // or a grace window expires.

})));

export default useRegistrationTeamStore;